const DB_NAME = "alumni-form-drafts";
const DB_VERSION = 1;
const STORE_NAME = "drafts";
const LS_PREFIX = "alumni-draft:";

let dbPromise = null;

function openDatabase() {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB not available"));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "key" });
      }
    };
  });

  return dbPromise;
}

export function getCurrentUserId() {
  try {
    const me = JSON.parse(localStorage.getItem("me") || "null");
    if (!me) return "anonymous";
    return String(me.id ?? me.uuid ?? me.pk ?? me.email ?? "anonymous");
  } catch {
    return "anonymous";
  }
}

export function getDraftKey(userId, namespace, formId) {
  const base = `form-draft:${userId}:${namespace}`;
  return formId != null && formId !== "" ? `${base}:${formId}` : base;
}

async function saveToIndexedDB(key, payload) {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    store.put({ key, payload, updatedAt: Date.now() });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function loadFromIndexedDB(key) {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const store = tx.objectStore(STORE_NAME);
    const request = store.get(key);
    request.onsuccess = () => resolve(request.result?.payload ?? null);
    request.onerror = () => reject(request.error);
  });
}

async function removeFromIndexedDB(key) {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    store.delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

function saveToLocalStorage(key, payload) {
  localStorage.setItem(`${LS_PREFIX}${key}`, JSON.stringify(payload));
}

function loadFromLocalStorage(key) {
  const raw = localStorage.getItem(`${LS_PREFIX}${key}`);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function removeFromLocalStorage(key) {
  localStorage.removeItem(`${LS_PREFIX}${key}`);
}

export async function saveDraft(key, payload) {
  const envelope = {
    version: 1,
    savedAt: new Date().toISOString(),
    ...payload,
  };

  try {
    await saveToIndexedDB(key, envelope);
    removeFromLocalStorage(key);
    return envelope;
  } catch (err) {
    console.warn("[formDraftStorage] IndexedDB save failed, using localStorage:", err);
    try {
      saveToLocalStorage(key, envelope);
      return envelope;
    } catch (lsErr) {
      console.warn("[formDraftStorage] localStorage save failed:", lsErr);
      return null;
    }
  }
}

export async function loadDraft(key) {
  try {
    const fromIdb = await loadFromIndexedDB(key);
    if (fromIdb) return fromIdb;
  } catch (err) {
    console.warn("[formDraftStorage] IndexedDB load failed:", err);
  }

  return loadFromLocalStorage(key);
}

export async function removeDraft(key) {
  try {
    await removeFromIndexedDB(key);
  } catch (err) {
    console.warn("[formDraftStorage] IndexedDB remove failed:", err);
  }
  removeFromLocalStorage(key);
}

export async function hasDraft(key) {
  const draft = await loadDraft(key);
  return draft != null;
}
